import { useId } from "react";
import type { TileField } from "@fortex/api";
import { useIntl, useLabels } from "../hooks";
import { dateInputValue, displayRecords, isPhotoField, isValidJson, jsonRecords, parseBool, photoJson, photoRefs, prettyJson } from "../fieldValues";
import { Input, TextArea } from "../primitives/Input";
import { Switch } from "../primitives/Switch";
import { PhotoField } from "./PhotoField";

export type FieldEditorProps = {
  field: TileField;
  value: string | null;
  editable: boolean;
  dirty?: boolean;
  childFields?: TileField[];
  admin?: boolean;
  searchTerm?: string | null;
  onChange: (value: string) => void;
};

export function fieldLabel(field: TileField): string {
  return field.humanName.trim().length > 0 ? field.humanName : field.key;
}

export function childVisible(child: TileField, admin: boolean): boolean {
  return child.visible === "All" || (child.visible === "Admin" && admin);
}

export function FieldEditor({ field, value, editable, dirty = false, childFields = [], admin = false, searchTerm = null, onChange }: FieldEditorProps) {
  const labels = useLabels();
  const intl = useIntl();
  const id = useId();
  const label = fieldLabel(field);
  const named = field.humanName.trim().length > 0;
  const canEdit = editable && field.editable;
  const classes = ["fxmap-field-row", `fxmap-field-row-${field.type.toLowerCase()}`, dirty ? "fxmap-field-row-dirty" : "", canEdit ? "fxmap-field-row-editable" : ""].filter(Boolean).join(" ");

  if (field.type === "Json") {
    if (isPhotoField(field.key, value)) {
      const ids = (photoRefs(value) ?? []).map(a => a.id);
      const title = named ? label : labels.photos;

      return (
        <div className={`${classes} fxmap-field-row-photos`} data-key={field.key}>
          <span className="fxmap-field-row-label fxmap-muted" id={id}>
            {title}
          </span>
          <PhotoField fieldKey={field.key} label={title} ids={ids} editable={canEdit} onChange={next => onChange(photoJson(next))} />
        </div>
      );
    }

    const stored = canEdit ? null : jsonRecords(value);
    const records = stored === null ? null : displayRecords(stored, field, admin, searchTerm);

    if (records !== null) {
      const names = new Map(childFields.map(a => [a.key, fieldLabel(a)] as const));
      const hidden = new Set(childFields.filter(a => !childVisible(a, admin)).map(a => a.key));

      return (
        <div className={`${classes} fxmap-field-row-records`} data-key={field.key}>
          {named ? (
            <span className="fxmap-field-row-label fxmap-muted" id={id}>
              {label}
            </span>
          ) : null}
          <div className="fxmap-field-records" role="list" aria-labelledby={named ? id : undefined}>
            {records.map((record, index) => (
              <div key={index} className="fxmap-field-record" role="listitem">
                {record
                  .filter(pair => !hidden.has(pair.key))
                  .map(pair => (
                    <div key={pair.key} className="fxmap-field-subrow">
                      <span className="fxmap-field-row-label fxmap-muted">{names.get(pair.key) ?? pair.key}</span>
                      <span className="fxmap-field-row-value fxmap-field-text">{pair.value}</span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const invalid = canEdit && !isValidJson(value ?? "");

    return (
      <details className={classes} data-key={field.key} open={canEdit}>
        <summary className="fxmap-field-row-label" id={id}>
          {label}
        </summary>
        {canEdit ? <TextArea aria-labelledby={id} mono rows={6} value={value ?? ""} invalid={invalid} onChange={event => onChange(event.target.value)} /> : <pre className="fxmap-field-json fxmap-mono">{prettyJson(value)}</pre>}
      </details>
    );
  }

  return (
    <div className={classes} data-key={field.key}>
      <span className="fxmap-field-row-label fxmap-muted" id={id}>
        {label}
      </span>
      <span className="fxmap-field-row-value">
        {field.type === "Bool" ? (
          canEdit ? (
            <Switch label={label} checked={parseBool(value)} onChange={checked => onChange(checked ? "true" : "false")} />
          ) : (
            <span className="fxmap-field-text">{parseBool(value) ? labels.yes : labels.no}</span>
          )
        ) : field.type === "Date" ? (
          canEdit ? (
            <Input type="date" aria-labelledby={id} value={dateInputValue(value)} onChange={event => onChange(event.target.value)} />
          ) : (
            <span className="fxmap-field-text fxmap-tabular">{value === null || value.length === 0 ? "" : intl.date(dateInputValue(value) || value)}</span>
          )
        ) : canEdit ? (
          <Input type="text" aria-labelledby={id} value={value ?? ""} onChange={event => onChange(event.target.value)} />
        ) : (
          <span className="fxmap-field-text">{value ?? ""}</span>
        )}
      </span>
    </div>
  );
}
